import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import { Dialog } from 'primereact/dialog'
import { useTranslation } from 'react-i18next'

// local imports - types
import calendarLoading from 'assets/icons/loading-calendar.png'
import { IFilteredCoiffeurList, IFilteredCoiffeurSalonList } from 'types/CoiffeurType'
import DateUtil from 'utils/DateUtil'
import useGetCoiffeurReservationTable from '../hooks/use-get-coiffeur-reservation-table'
import Loader from 'components/ui/Loader'
import WeekSlider from '../../health-service/appointment-scheduler/WeekSlider'
import ReservationModalHeader from './ReservationModalHeader'
import ReservationScheduler from './ReservationScheduler'
import CoiffeurScheduleSidebar from './ScheduleSidebar'
import { ActionArea, CancelButton, ConfirmButton } from './styled'

/* Types */
interface IReservationModalProps {
  salon: IFilteredCoiffeurSalonList
  filterDate: Date
  subServiceId: number
  locationId: number
  coiffeurs: IFilteredCoiffeurList[] | []
  selectedCoiffeur: IFilteredCoiffeurList
  setSelectedCoiffeur: Dispatch<SetStateAction<any | undefined>>
  openFormModal: () => void
  open: boolean
  closeReservationModal: () => void
  openReservationModal: () => void
  showNotification: () => void
}

interface IModalFooterProps {
  disabled: boolean
  onCancel: () => void
  onSave: () => void
}

function FooterComponent({ onSave, onCancel, disabled }: IModalFooterProps) {
  const { t } = useTranslation()
  return (
    <ActionArea>
      <CancelButton id="closeModal" className="p-button-outlined" label={t('buttons.cancel')} onClick={onCancel} />
      <ConfirmButton id="openSidebar" disabled={disabled} label={t('buttons.book-reservation')} onClick={onSave} />
    </ActionArea>
  )
}

export default function ReservationModalWrapper({
  salon,
  filterDate: dateFromList,
  subServiceId,
  locationId,
  coiffeurs,
  selectedCoiffeur,
  setSelectedCoiffeur,
  openFormModal,
  open,
  closeReservationModal,
  openReservationModal: openModal,
  showNotification,
}: IReservationModalProps) {
  const { t } = useTranslation()

  /* Coiffeur List reservationDate filter value for appointment date filtering etc. */
  const [filterDate, setFilterDate] = useState<Date>(dateFromList)
  useEffect(() => setFilterDate(dateFromList), [dateFromList])
  const lastFilteredDate = DateUtil.getWeekStartEndFromDate(dateFromList)

  /* GET Selected Coiffeur's Reservation Table List | Custom Hook  */
  const {
    availableDates,
    tableWeek,
    setTableWeek,
    selectedReservation,
    setSelectedReservation,
    sidebarVisible,
    setSidebarVisible,
    loading,
    dailyReservations,
  } = useGetCoiffeurReservationTable(selectedCoiffeur.id, subServiceId, dateFromList, locationId)

  /* Open Appointment Sidebar */
  const openAppointmentSidebar = () => {
    setSidebarVisible(true)
    closeReservationModal()
  }

  /* Close Sidebar and reset states */
  const closeSidebarAfterSave = () => {
    setSidebarVisible(false)
    setSelectedReservation(undefined)
    showNotification()
    setFilterDate(dateFromList)
    setTableWeek(lastFilteredDate)
  }

  /* Close with cancel */
  const handleCloseOnCancel = () => {
    closeReservationModal()
    openFormModal()
    setFilterDate(dateFromList)
    setSelectedReservation(undefined)
    setTableWeek(lastFilteredDate)
  }

  return (
    <>
      <Dialog
        id="appointmentModal"
        blockScroll
        keepInViewport
        closeOnEscape={false}
        focusOnShow={false}
        closable={true}
        header={<span>{t('dialog-texts.coiffeur-reservation-header')} (2/2)</span>}
        draggable={false}
        resizable={false}
        visible={open}
        style={{ width: '95vw', height: '95vh' }}
        breakpoints={{ '1536px': '95vw', '1280': '95vw' }}
        onHide={handleCloseOnCancel}
        footer={
          <FooterComponent
            onSave={openAppointmentSidebar}
            onCancel={handleCloseOnCancel}
            disabled={!selectedReservation}
          />
        }>
        {/* Modal Header */}
        <ReservationModalHeader
          subServiceId={subServiceId}
          salon={salon}
          filterDate={filterDate}
          availableDateRange={availableDates}
          setFilterDate={setFilterDate}
          setSelectedCoiffeur={setSelectedCoiffeur}
          selectedCoiffeur={selectedCoiffeur}
          coiffeurs={coiffeurs}
          setTableWeek={setTableWeek}
        />

        {/* Week Change Slider */}
        <WeekSlider availableDateRange={availableDates} tableWeek={tableWeek} setTableWeek={setTableWeek} />

        {/* Content - Calendar View */}
        {loading ? (
          <Loader src={calendarLoading} />
        ) : (
          <ReservationScheduler
            filterDate={filterDate}
            setFilterDate={setFilterDate}
            tableWeek={tableWeek}
            dailyReservations={dailyReservations}
            selectedReservation={selectedReservation}
            setSelectedReservation={setSelectedReservation}
          />
        )}
      </Dialog>

      {/* Schedule - Save Appointment Sidebar */}
      {!!selectedReservation && (
        <CoiffeurScheduleSidebar
          salon={salon}
          subServiceId={subServiceId}
          openModal={openModal}
          visible={sidebarVisible}
          setVisible={setSidebarVisible}
          closeAfterSave={closeSidebarAfterSave}
          coiffeurInfo={selectedCoiffeur}
          reservationInfo={selectedReservation}
        />
      )}
    </>
  )
}
